import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Target, Award, CheckCircle, Clock, Calendar, 
  Trophy, Lock, AlertTriangle, ChevronDown, ChevronUp,
  FileText, Download, Shield, RotateCcw, MessageSquare
} from 'lucide-react';

const CompetencesPrerequisConsultation = ({ stagiaireId, hideEpmsp = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});
  const [epmspDocs, setEpmspDocs] = useState({ declaration: null, attestation: null });
  const [epmspData, setEpmspData] = useState(null);
  const [grilleDetails, setGrilleDetails] = useState({});

  const PREREQUIS = [
    "Parcours en jet à selle",
    "Parcours en jet à bras",
    "Parcours en engins tractés"
  ];

  const BLOCS_COMPETENCES = [
    { id: "BC1", nom: "Projet d'animation" },
    { id: "BC2", nom: "Valorisation des activités" },
    { id: "BC3", nom: "Séance d'animation" }
  ];

  const EPREUVES_EPMSP = [
    "Séances de découverte VNM à selle",
    "Traction d'un engin tracté",
    "Situation de secours",
    "Séance d'initiation sur support au choix (ESH/PNM/Jet à Bras)"
  ];

  useEffect(() => {
    if (stagiaireId) {
      loadData();
      loadEpmspDocs();
      loadEpmspData();
    }
  }, [stagiaireId]);

  const loadData = async () => {
    try {
      const response = await axios.get(`/stagiaires/${stagiaireId}/competences-prerequis`);
      setData(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadEpmspDocs = async () => {
    try {
      const docsRes = await axios.get(`/documents/${stagiaireId}`);
      const docs = docsRes.data || [];
      const declaration = docs.find(d => d.document_type === 'declaration_epmsp' || d.type_document === 'declaration_epmsp');
      const attestation = docs.find(d => d.document_type === 'attestation_epmsp' || d.type_document === 'attestation_epmsp');
      setEpmspDocs({ declaration, attestation });
    } catch {}
  };

  const loadEpmspData = async () => {
    try {
      const response = await axios.get(`/epmsp/stagiaire/${stagiaireId}`);
      setEpmspData(response.data);
      
      // Charger les détails des grilles liées aux validations
      const epreuves = response.data?.epreuves || {};
      const grilleIds = new Set();
      Object.values(epreuves).forEach(v => {
        if (v.grille_id) grilleIds.add(v.grille_id);
      });
      const details = {};
      for (const gId of grilleIds) {
        try {
          const gRes = await axios.get(`/epmsp/grille/stagiaire/${stagiaireId}`);
          const grilles = gRes.data || [];
          grilles.forEach(g => { details[g.grille_id] = g; });
          break;
        } catch {}
      }
      setGrilleDetails(details);
    } catch {}
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const docId = doc.document_id || doc.doc_id;
      if (docId) {
        const res = await axios.get(`/documents/download/${docId}`);
        if (res.data?.file_data) {
          const byteChars = atob(res.data.file_data);
          const byteNums = new Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
          const blob = new Blob([new Uint8Array(byteNums)], { type: res.data.content_type || 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.filename || doc.nom || 'document.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch {
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'valide':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
          <CheckCircle className="h-3 w-3" /> Validé
        </span>;
      case 'rattrapage':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">En rattrapage</span>;
      case 'equivalence':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">Équivalence</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full flex items-center gap-1">
          <Clock className="h-3 w-3" /> En attente
        </span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-24">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="competences-prerequis-consultation">
      
      {/* ====== SECTION 1: EPMSP ====== */}
      {!hideEpmsp && (
      <div className="bg-white border border-orange-200 rounded-xl overflow-hidden shadow-sm">
        <div 
          className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 flex items-center justify-between cursor-pointer hover:from-orange-100 hover:to-amber-100 transition-colors"
          onClick={() => toggleSection('epmsp')}
        >
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 rounded-lg p-2">
              <Shield className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-[#0F172A]">EPMSP</span>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="w-24 h-1.5 bg-orange-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${(epmspData?.validated_count || 0) === 4 ? 'bg-green-500' : 'bg-orange-500'}`}
                    style={{ width: `${((epmspData?.validated_count || 0) / 4) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-bold ${(epmspData?.validated_count || 0) === 4 ? 'text-green-600' : 'text-orange-600'}`}>
                  {epmspData?.validated_count || 0}/4
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(epmspData?.validated_count || 0) === 4 && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                <CheckCircle className="h-3 w-3" /> Validé
              </span>
            )}
            {(epmspDocs.declaration || epmspDocs.attestation) && !(epmspDocs.declaration && epmspDocs.attestation) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium animate-pulse">
                1 doc reçu
              </span>
            )}
            {epmspDocs.declaration && epmspDocs.attestation && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                <FileText className="h-3 w-3" /> Docs complets
              </span>
            )}
            {expandedSections.epmsp ? <ChevronUp className="h-4 w-4 text-orange-500" /> : <ChevronDown className="h-4 w-4 text-orange-500" />}
          </div>
        </div>

        {expandedSections.epmsp && (
          <div className="p-3 space-y-3 border-t border-orange-100">
            {/* Détail des 4 épreuves EPMSP */}
            {EPREUVES_EPMSP.map((epreuve, index) => {
              const validation = epmspData?.epreuves?.[epreuve] || { statut: 'en_attente' };
              const isValide = validation.statut === 'valide' || validation.statut === 'rattrapage_valide';
              const isRattrapage = validation.statut === 'rattrapage';
              return (
                <div key={index} className={`p-3 rounded-lg border ${
                  isValide ? 'bg-green-50 border-green-200'
                    : isRattrapage ? 'bg-orange-50 border-orange-200'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#0F172A] flex-1">{index + 1}. {epreuve}</span>
                    {isValide ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" /> {validation.statut === 'rattrapage_valide' ? 'Validé (2e)' : 'Validé'}
                      </span>
                    ) : isRattrapage ? (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium flex items-center gap-1">
                        <RotateCcw className="h-3 w-3" /> Rattrapage
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full flex items-center gap-1">
                        <Clock className="h-3 w-3" /> En attente
                      </span>
                    )}
                  </div>
                  {validation.date_epreuve && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />
                      {new Date(validation.date_epreuve).toLocaleDateString('fr-FR')}
                      {validation.support_choisi && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">{validation.support_choisi}</span>}
                    </div>
                  )}
                  {validation.commentaire && (
                    <div className="mt-2 flex items-start gap-2 text-xs text-slate-600 bg-white/50 p-2 rounded">
                      <MessageSquare className="h-3 w-3 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span>"{validation.commentaire}"</span>
                    </div>
                  )}
                  {/* Détails grille : remarques détaillées et évaluateurs */}
                  {validation.grille_id && grilleDetails[validation.grille_id] && (() => {
                    const grille = grilleDetails[validation.grille_id];
                    const evals = grille.evaluations || {};
                    const remarquesDetail = Object.entries(evals)
                      .filter(([, ev]) => ev.remarques)
                      .map(([key, ev]) => ({ key, remarques: ev.remarques, statut: ev.statut }));
                    const evaluateurs = grille.evaluateurs || [];
                    if (remarquesDetail.length === 0 && evaluateurs.length === 0) return null;
                    return (
                      <div className="mt-2 space-y-1.5">
                        {remarquesDetail.map((r, ri) => (
                          <div key={ri} className="flex items-start gap-2 text-xs text-slate-500 bg-white/60 p-1.5 rounded">
                            <FileText className="h-3 w-3 mt-0.5 text-slate-300 flex-shrink-0" />
                            <span className="italic">{r.remarques}</span>
                          </div>
                        ))}
                        {evaluateurs.length > 0 && (
                          <div className="text-xs text-slate-400 flex items-center gap-1">
                            <Award className="h-3 w-3" />
                            Évaluateur(s): {evaluateurs.map(e => `${e.prenom || ''} ${e.nom || ''}`).join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}

            {/* Séparateur + Documents EPMSP */}
            <div className="border-t border-orange-100 pt-3 mt-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Documents EPMSP</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Déclaration stagiaire */}
                <div className={`p-3 rounded-lg border ${epmspDocs.declaration ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200 border-dashed'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className={`h-4 w-4 ${epmspDocs.declaration ? 'text-green-600' : 'text-orange-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-[#0F172A]">Déclaration stagiaire</p>
                        <p className="text-xs mt-0.5 ${epmspDocs.declaration ? 'text-green-600' : 'text-orange-600'}">
                          {epmspDocs.declaration ? `Déposée le ${new Date(epmspDocs.declaration.uploaded_at || epmspDocs.declaration.date_upload || '').toLocaleDateString('fr-FR')}` : 'Non déposée'}
                        </p>
                      </div>
                    </div>
                    {epmspDocs.declaration ? (
                      <button onClick={() => handleDownloadDoc(epmspDocs.declaration)} className="text-blue-600 hover:text-blue-800 p-1">
                        <Download className="h-4 w-4" />
                      </button>
                    ) : (
                      <Clock className="h-4 w-4 text-orange-400" />
                    )}
                  </div>
                </div>

                {/* Attestation EPMSP */}
                <div className={`p-3 rounded-lg border ${epmspDocs.attestation ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200 border-dashed'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className={`h-4 w-4 ${epmspDocs.attestation ? 'text-green-600' : 'text-orange-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-[#0F172A]">Attestation EPMSP</p>
                        <p className="text-xs mt-0.5 ${epmspDocs.attestation ? 'text-green-600' : 'text-orange-600'}">
                          {epmspDocs.attestation ? `Déposée le ${new Date(epmspDocs.attestation.uploaded_at || epmspDocs.attestation.date_upload || '').toLocaleDateString('fr-FR')}` : 'Non déposée'}
                        </p>
                      </div>
                    </div>
                    {epmspDocs.attestation ? (
                      <button onClick={() => handleDownloadDoc(epmspDocs.attestation)} className="text-blue-600 hover:text-blue-800 p-1">
                        <Download className="h-4 w-4" />
                      </button>
                    ) : (
                      <Clock className="h-4 w-4 text-orange-400" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* ====== SECTION 2: PREREQUIS ====== */}
      <div className="bg-white border border-purple-200 rounded-xl overflow-hidden shadow-sm">
        <div 
          className="p-4 bg-gradient-to-r from-purple-50 to-purple-100/50 flex items-center justify-between cursor-pointer hover:from-purple-100 hover:to-purple-100 transition-colors"
          onClick={() => toggleSection('prerequis')}
        >
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 rounded-lg p-2">
              <Target className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <span className="font-semibold text-sm text-[#0F172A]">Prérequis</span>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="w-24 h-1.5 bg-purple-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${data.all_prerequis_validated ? 'bg-green-500' : 'bg-purple-500'}`}
                    style={{ width: `${(data.prerequis_validated_count / data.prerequis_total) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-bold ${data.all_prerequis_validated ? 'text-green-600' : 'text-purple-600'}`}>
                  {data.prerequis_validated_count}/{data.prerequis_total}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data.all_prerequis_validated && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                <CheckCircle className="h-3 w-3" /> Validés
              </span>
            )}
            {expandedSections.prerequis ? <ChevronUp className="h-4 w-4 text-purple-500" /> : <ChevronDown className="h-4 w-4 text-purple-500" />}
          </div>
        </div>

        {expandedSections.prerequis && (
          <div className="p-3 space-y-2 border-t border-purple-100">
            {PREREQUIS.map((prereq) => {
              const validation = data.prerequis[prereq] || { statut: 'en_attente' };
              return (
                <div key={prereq} className={`p-3 rounded-lg border ${
                  validation.statut === 'valide' || validation.statut === 'equivalence'
                    ? 'bg-green-50 border-green-200'
                    : validation.statut === 'rattrapage' ? 'bg-orange-50 border-orange-200'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#0F172A]">{prereq}</span>
                    {getStatusBadge(validation.statut)}
                  </div>
                  {validation.date_validation && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />
                      {new Date(validation.date_validation).toLocaleDateString('fr-FR')}
                    </div>
                  )}
                  {validation.commentaire && (
                    <p className="mt-2 text-xs text-slate-600 italic bg-white/50 p-2 rounded">"{validation.commentaire}"</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ====== SECTION 3: BLOCS DE COMPETENCES ====== */}
      <div className="bg-white border border-[#8DC63F]/30 rounded-xl overflow-hidden shadow-sm">
        <div 
          className="p-4 bg-gradient-to-r from-[#8DC63F]/10 to-[#8DC63F]/5 flex items-center justify-between cursor-pointer hover:from-[#8DC63F]/20 hover:to-[#8DC63F]/10 transition-colors"
          onClick={() => toggleSection('bc')}
        >
          <div className="flex items-center gap-3">
            <div className="bg-[#8DC63F]/20 rounded-lg p-2">
              {data.diplome_obtenu ? <Trophy className="h-5 w-5 text-green-600" /> : <Award className="h-5 w-5 text-[#8DC63F]" />}
            </div>
            <div>
              <span className="font-semibold text-sm text-[#0F172A]">Blocs de Compétences</span>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="w-24 h-1.5 bg-[#8DC63F]/20 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${data.diplome_obtenu ? 'bg-green-500' : 'bg-[#8DC63F]'}`}
                    style={{ width: `${(data.bc_validated_count / data.bc_total) * 100}%` }}
                  />
                </div>
                <span className={`text-xs font-bold ${data.diplome_obtenu ? 'text-green-600' : 'text-[#8DC63F]'}`}>
                  {data.bc_validated_count}/{data.bc_total}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {data.diplome_obtenu && (
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                <Trophy className="h-3 w-3" /> Diplôme
              </span>
            )}
            {expandedSections.bc ? <ChevronUp className="h-4 w-4 text-[#8DC63F]" /> : <ChevronDown className="h-4 w-4 text-[#8DC63F]" />}
          </div>
        </div>

        {expandedSections.bc && (
          <div className="p-3 space-y-2 border-t border-[#8DC63F]/20">
            {BLOCS_COMPETENCES.map((bloc) => {
              const validation = data.blocs_competences[bloc.id] || { statut: 'en_attente' };
              const isBC3Blocked = bloc.id === 'BC3' && !data.all_prerequis_validated;
              return (
                <div key={bloc.id} className={`p-3 rounded-lg border ${
                  isBC3Blocked ? 'bg-slate-100 border-slate-300 opacity-75'
                    : validation.statut === 'valide' || validation.statut === 'equivalence' ? 'bg-green-50 border-green-200'
                    : validation.statut === 'rattrapage' ? 'bg-orange-50 border-orange-200'
                    : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                        validation.statut === 'valide' || validation.statut === 'equivalence' ? 'bg-green-600 text-white' : 'bg-slate-600 text-white'
                      }`}>{bloc.id}</span>
                      <span className="text-sm font-medium text-[#0F172A]">{bloc.nom}</span>
                      {isBC3Blocked && <Lock className="h-3 w-3 text-slate-400" />}
                    </div>
                    {isBC3Blocked ? (
                      <span className="px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded-full flex items-center gap-1">
                        <Lock className="h-3 w-3" /> Bloqué
                      </span>
                    ) : getStatusBadge(validation.statut)}
                  </div>
                  {isBC3Blocked && (
                    <p className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Nécessite la validation de tous les prérequis
                    </p>
                  )}
                  {!isBC3Blocked && validation.date_validation && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />
                      {new Date(validation.date_validation).toLocaleDateString('fr-FR')}
                      {validation.is_rattrapage && <span className="text-orange-600">(Rattrapage)</span>}
                    </div>
                  )}
                  {!isBC3Blocked && validation.commentaire && (
                    <p className="mt-2 text-xs text-slate-600 italic bg-white/50 p-2 rounded">"{validation.commentaire}"</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetencesPrerequisConsultation;
